import type { ExportFormat, ImportEntry, JsonKeyConfig } from "@/types";

import Encoding from "encoding-japanese";
import Papa from "papaparse";

export interface ValidatedEntry {
  before: string;
  after: string;
  valid: boolean;
  error: string;
}

export const validateEntry = (entry: ImportEntry): ValidatedEntry => {
  const before = entry.before.trim();
  const after = entry.after.trim();
  if (!before) return { before, after, valid: false, error: "変換前が空です" };
  if (before.length > 60)
    return { before, after, valid: false, error: "変換前は60文字以内にしてください" };
  if (/^[\x20-\x7e]$/.test(before))
    return { before, after, valid: false, error: "半角文字は2文字以上にしてください" };
  if (after.length > 60)
    return { before, after, valid: false, error: "変換後は60文字以内にしてください" };
  return { before, after, valid: true, error: "" };
};

export const getCsvHeaders = (text: string, delimiter: string, hasHeader: boolean): string[] => {
  const result = Papa.parse<string[]>(text, {
    delimiter,
    header: false,
    preview: 1,
    skipEmptyLines: true,
  });
  if (!result.data.length) return [];
  const firstRow = result.data[0];
  if (hasHeader) return firstRow;
  return Array.from({ length: firstRow.length }, (_, i) => `列${i + 1}`);
};

export const parseCsvTsv = (
  text: string,
  options: { delimiter: string; hasHeader: boolean; beforeKey: string; afterKey: string },
): ImportEntry[] => {
  const { delimiter, hasHeader, beforeKey, afterKey } = options;
  if (!beforeKey || !afterKey) return [];
  if (hasHeader) {
    const result = Papa.parse<Record<string, string>>(text, {
      delimiter,
      header: true,
      skipEmptyLines: true,
    });
    const fields = result.meta.fields ?? [];
    if (!fields.includes(beforeKey) || !fields.includes(afterKey)) return [];
    return result.data.map((row) => ({ before: row[beforeKey] ?? "", after: row[afterKey] ?? "" }));
  } else {
    const result = Papa.parse<string[]>(text, { delimiter, header: false, skipEmptyLines: true });
    const matchBefore = /^列(\d+)$/.exec(beforeKey);
    const matchAfter = /^列(\d+)$/.exec(afterKey);
    if (!matchBefore || !matchAfter) return [];
    const beforeIdx = parseInt(matchBefore[1]) - 1;
    const afterIdx = parseInt(matchAfter[1]) - 1;
    return result.data
      .filter(
        (row) => beforeIdx >= 0 && afterIdx >= 0 && beforeIdx < row.length && afterIdx < row.length,
      )
      .map((row) => ({ before: row[beforeIdx], after: row[afterIdx] }));
  }
};

export const parseJson = (
  text: string,
  options: JsonKeyConfig,
): { entries: ImportEntry[]; error: string | null } => {
  try {
    const parsed: unknown = JSON.parse(text);
    if (!Array.isArray(parsed)) {
      return { entries: [], error: "JSONはトップレベルが配列である必要があります" };
    }
    const result: ImportEntry[] = [];
    for (const item of parsed) {
      if (Array.isArray(item)) {
        const [first, second] = item as unknown[];
        result.push({ before: String(first ?? ""), after: String(second ?? "") });
      } else if (item !== null && typeof item === "object") {
        const obj = item as Record<string, unknown>;
        result.push({
          before: String(obj[options.beforeKey] ?? ""),
          after: String(obj[options.afterKey] ?? ""),
        });
      }
    }
    return { entries: result, error: null };
  } catch {
    return { entries: [], error: "JSONの解析に失敗しました" };
  }
};

export const detectFormatFromFile = (file: File): ExportFormat => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "tsv") return "tsv";
  if (ext === "json") return "json";
  return "csv";
};

const normalizeHeaderCell = (cell: string): string => cell.replace(/^\uFEFF/, "").trim();

export const hasVoiscordHeader = (text: string, delimiter: string): boolean => {
  const firstRow = getCsvHeaders(text, delimiter, true).map(normalizeHeaderCell);
  return firstRow.includes("変換前") && firstRow.includes("変換後");
};

export type TxtDetectResult = {
  format: ExportFormat;
  delimiter: "," | "\t";
  hasHeader: boolean;
  beforeKey: string;
  afterKey: string;
  jsonKeyConfig: JsonKeyConfig;
  error: string | null;
};

const tryCsvTsv = (
  text: string,
  delimiter: string,
  hasHeader: boolean,
): { beforeKey: string; afterKey: string } | null => {
  const headers = getCsvHeaders(text, delimiter, hasHeader);
  if (headers.length < 2) return null;
  const entries = parseCsvTsv(text, {
    delimiter,
    hasHeader,
    beforeKey: headers[0],
    afterKey: headers[1],
  });
  if (entries.length === 0) return null;
  return { beforeKey: headers[0], afterKey: headers[1] };
};

const detectVoiscordHeaderKeyConfig = (text: string, delimiter: string): JsonKeyConfig | null => {
  const parsed = Papa.parse<string[]>(text, {
    delimiter,
    preview: 1,
    skipEmptyLines: true,
  });
  const headerRow = parsed.data[0];
  if (!Array.isArray(headerRow)) {
    return null;
  }

  const normalizedHeaders = headerRow.map((header, index) => ({
    index,
    original: index === 0 ? header.replace(/^\uFEFF/, "") : header,
    normalized: (index === 0 ? header.replace(/^\uFEFF/, "") : header).trim(),
  }));
  const beforeHeader = normalizedHeaders.find((_1) => _1.normalized === "変換前");
  const afterHeader = normalizedHeaders.find((_1) => _1.normalized === "変換後");

  if (!beforeHeader || !afterHeader) {
    return null;
  }

  return {
    beforeKey: beforeHeader.original,
    afterKey: afterHeader.original,
  };
};

export const detectTxtFormat = (text: string): TxtDetectResult => {
  const defaultJsonKeyConfig: JsonKeyConfig = { beforeKey: "before", afterKey: "after" };

  // CSV（VOISCORD ヘッダーあり）
  if (hasVoiscordHeader(text, ",")) {
    const csvWithHeader = tryCsvTsv(text, ",", true);
    if (csvWithHeader) {
      const headerKeyConfig = detectVoiscordHeaderKeyConfig(text, ",") ?? defaultJsonKeyConfig;
      return {
        format: "csv",
        delimiter: ",",
        hasHeader: true,
        ...csvWithHeader,
        beforeKey: headerKeyConfig.beforeKey,
        afterKey: headerKeyConfig.afterKey,
        jsonKeyConfig: headerKeyConfig,
        error: null,
      };
    }
  }

  // TSV（VOISCORD ヘッダーあり）
  if (hasVoiscordHeader(text, "\t")) {
    const tsvWithHeader = tryCsvTsv(text, "\t", true);
    if (tsvWithHeader) {
      const headerKeyConfig = detectVoiscordHeaderKeyConfig(text, "\t") ?? defaultJsonKeyConfig;
      return {
        format: "tsv",
        delimiter: "\t",
        hasHeader: true,
        ...tsvWithHeader,
        beforeKey: headerKeyConfig.beforeKey,
        afterKey: headerKeyConfig.afterKey,
        jsonKeyConfig: headerKeyConfig,
        error: null,
      };
    }
  }

  // JSON（[ または { で始まる場合は CSV/TSV より先に試みる。誤検出防止）
  const trimmed = text.trimStart();
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    const jsonEarlyResult = parseJson(text, defaultJsonKeyConfig);
    if (jsonEarlyResult.error === null) {
      let jsonKeyConfig = defaultJsonKeyConfig;
      try {
        const parsed = JSON.parse(text) as unknown[];
        const first = Array.isArray(parsed) ? parsed[0] : null;
        if (first !== null && typeof first === "object" && !Array.isArray(first)) {
          const keys = Object.keys(first as object);
          if (keys.length >= 2) {
            jsonKeyConfig = { beforeKey: keys[0], afterKey: keys[1] };
          } else if (keys.length === 1) {
            jsonKeyConfig = { beforeKey: keys[0], afterKey: keys[0] };
          }
        }
      } catch {
        // ignore
      }
      return {
        format: "json",
        delimiter: ",",
        hasHeader: true,
        beforeKey: "",
        afterKey: "",
        jsonKeyConfig,
        error: null,
      };
    }
  }

  // CSV（ヘッダーなし）
  const csvNoHeader = tryCsvTsv(text, ",", false);
  if (csvNoHeader) {
    return {
      format: "csv",
      delimiter: ",",
      hasHeader: false,
      ...csvNoHeader,
      jsonKeyConfig: defaultJsonKeyConfig,
      error: null,
    };
  }

  // TSV（ヘッダーなし）
  const tsvNoHeader = tryCsvTsv(text, "\t", false);
  if (tsvNoHeader) {
    return {
      format: "tsv",
      delimiter: "\t",
      hasHeader: false,
      ...tsvNoHeader,
      jsonKeyConfig: defaultJsonKeyConfig,
      error: null,
    };
  }

  // JSON（上記で検出できなかった場合）
  const jsonResult = parseJson(text, defaultJsonKeyConfig);
  if (jsonResult.error === null) {
    let jsonKeyConfig = defaultJsonKeyConfig;
    try {
      const parsed = JSON.parse(text) as unknown[];
      const first = Array.isArray(parsed) ? parsed[0] : null;
      if (first !== null && typeof first === "object" && !Array.isArray(first)) {
        const keys = Object.keys(first as object);
        if (keys.length >= 2) {
          jsonKeyConfig = { beforeKey: keys[0], afterKey: keys[1] };
        } else if (keys.length === 1) {
          jsonKeyConfig = { beforeKey: keys[0], afterKey: keys[0] };
        }
      }
    } catch {
      // ignore
    }
    return {
      format: "json",
      delimiter: ",",
      hasHeader: true,
      beforeKey: "",
      afterKey: "",
      jsonKeyConfig,
      error: null,
    };
  }

  return {
    format: "csv",
    delimiter: ",",
    hasHeader: true,
    beforeKey: "",
    afterKey: "",
    jsonKeyConfig: defaultJsonKeyConfig,
    error: "ファイルの形式を自動判定できませんでした",
  };
};

export const decodeFile = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const uint8 = new Uint8Array(buffer);
  const detected = Encoding.detect(uint8);
  const detectedEncoding = typeof detected === "string" ? detected : "UTF8";
  const unicodeArray = Encoding.convert(uint8, { to: "UNICODE", from: detectedEncoding });
  return Encoding.codeToString(unicodeArray);
};
